import { ViewType, ViewObject } from '../base';

import { dataService } from '../../data';
import { Offer } from '../objects/offer';
import { OfferPeriod } from '../objects/offerperiod';
import { OfferProduct } from '../objects/offerproduct';
import { I18nValue } from '../objects/i18nvalue';

import { PlaceView } from './place';
import { ProductView } from './product';
import { ProductOptView } from './productopt';
import { TicketView } from './ticket';
import { TicketProductView } from './ticketproduct';

/**
 * View-managed projection of one menu product group (OfferProduct.grouped).
 * Used when browsing/applying menus into the ticket. Not a ViewObject: there is
 * no MENUGROUP DataObject. Identity is stable within an OfferView.groups cache
 * generation (same instance until RefreshView clears it).
 */
export class MenuView {
    constructor(
        private readonly _offer: OfferView,
        readonly index: number,
        readonly first: boolean,
        private _name: I18nValue | null,
        private readonly _products: ProductView[],
    ) {}

    get _uuid(): string {
        return this._offer._uuid + '-g' + this.index;
    }

    get name(): I18nValue | null {
        return this._name;
    }

    set name(value: I18nValue | null) {
        this._name = value;
    }

    get products(): ProductView[] {
        return this._products;
    }

    /** Owning offer view (canonical proxy from alives). */
    get offer(): OfferView {
        return this._offer;
    }
}

/****************************/
/* CLASS VIEW               */
/****************************/

export class _OfferView extends ViewObject<Offer> {
    
    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get offer(): Offer{
        return this.object;
    }
             
    constructor(object: Offer, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _validperiods: Array <OfferPeriod> | null = null;
    private _groupproducts: Array <MenuView> | null = null;
    private _validproducts: Array <OfferProduct> | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('OFFER')){
            this.DoRefreshView();
        }

        if (targets.includes('OFFERPERIOD')){
            this._validperiods = null;
            this.DoRefreshView();
        }

        if (targets.includes('OFFERPRODUCT')){
            this._validproducts = null;
            this._groupproducts = null;
            this.DoRefreshView();
        }

        if (targets.includes('I18NVALUE')){
            this._validproducts = null;
            this._groupproducts = null;
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCT')){
            this._validproducts = null;
            this._groupproducts = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        // update registrations when children change   
        this._subscriptions.push(
            this.offer.OnChildChanged.subscribe(() => {
                const self = weakref.deref();
                if (self) self.doRegister();
            })
        );

        // register to updates on contained items             
        this._subscriptions.push(
            this.offer.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }
            })
        )

        for(const product of this.products){
            this._subscriptions.push(
                product.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })
            );
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/
    
    get place(): PlaceView | null {
        return this.data.alives.get(this.offer.place) as PlaceView;
    }

    get periods(): Array <OfferPeriod> {
        if (this._validperiods == null){
            const _periods: Array <OfferPeriod> = []; 

            for(const _period of this.offer.periods){
                if (_period.IsValid){
                    _periods.push(_period);
                }
            }

            this._validperiods = _periods;
        }

        return this._validperiods;
    }

    get products(): Array <OfferProduct> {
        if (this._validproducts == null){
            const _products: Array <OfferProduct> = []; 

            for(const _product of this.offer.products){
                if (_product.IsValid) {
                    _products.push(_product);
                }
            }

            _products.sort((a, b) => {
                const _agroup = a.grouped || 0;
                const _bgroup = b.grouped || 0;

                return (_agroup - _bgroup);
            })

            this._validproducts = _products;
        }

        return this._validproducts;
    }

    get groups(): Array <MenuView> {
        if (this._groupproducts === null){
            const _offerView = this.data.alives.get(this.offer) as OfferView;
            const _groups = new Map<number, MenuView> ();
            for (const _offerproduct of this.products){
                const _grouped: number = _offerproduct.grouped || 0;

                let _groupnf: MenuView | null = _groups.get(_grouped) || null;
                if (!_groupnf){
                    const _first = (_groups.size == 0);
                    
                    _groupnf = new MenuView(
                        _offerView,
                        _grouped,
                        _first,
                        _offerproduct.menuctg,
                        []
                    );

                    _groups.set(_grouped, _groupnf);
                }

                const _productview = this.data.alives.get(_offerproduct.product) as ProductView;
                if (_productview?.last){
                    _groupnf.products.push(_productview.last);
                }
            }
                
            const _groupproducts = Array.from(_groups.values());
            _groupproducts.sort((a, b) => {
                return a.index - b.index;
            });

            this._groupproducts = _groupproducts;
        }

        return this._groupproducts;
    }

    /**********************************/
    /* PRODUCT TYPE                   */
    /**********************************/
    
    get IsOffer(): boolean {
        return !this.offer.ismenu || true;
    }

    get IsMenu(): boolean {
        return this.offer.ismenu || false;
    }

    get IsDiscount(): boolean {
        return false;
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/
    
    get IsAvailable(): boolean {
        return (this.offer.status == 'AC');
    }

    get IsActive(): boolean {
        if (!this.offer.IsValid){
            return false;
        }

        if (this.offer.allday){
            return true;
        }

        const _now = new Date();
        for (const period of this.periods){
            if (period.IsActiveAt(_now)){
                return true;
            }
        }

        return false;
    }

    /**********************************/
    /* OFFER APPLICATION              */
    /**********************************/

    AppliesTo(ticket: TicketView): Map <TicketProductView, number> | null {
        const ticketproducts = ticket.products.slice(0);

        ticketproducts.sort((a, b) => {
            const aprice: number = a.price || 0; 
            const bprice: number = b.price || 0; 

            return bprice - aprice;     // sort by price (descending)
        })

        if (!this.groups || this.groups.length == 0){
            console.warn("[OFFER] offer '" + (this.offer.name?.en || '') + "' does not provide any groups");
            return null;                
        }

        // One ticket line per group: any product listed in the group can fill it
        // (OR within the group). All groups must be satisfied for the offer to apply.
        const appliedproducts = new Map <TicketProductView, number> ();
        for (const groupinfo of this.groups) {
            let appliedproduct: TicketProductView | null = null;

            for (const ticketproduct of ticketproducts){
                if (!ticketproduct.product || !ticketproduct.InCart || ticketproduct.offer){
                    continue;
                }

                const applied = appliedproducts.get(ticketproduct) || 0;
                if (applied >= ticketproduct.amount){
                    continue;   // this ticketproduct does not have items left
                }

                const matchesgroup = groupinfo.products.some(
                    (groupproduct) => ticketproduct.product!.IsChild(groupproduct.object)
                );
                if (!matchesgroup){
                    continue;
                }

                appliedproducts.set(ticketproduct, applied + 1);
                appliedproduct = ticketproduct;
                break;  // one element applied on the group: switch to next group
            }

            if (!appliedproduct){
                return null;    // no ticketproducts in this offer group (not applicable)
            }
        }

        return appliedproducts;
    }

    SavingAmount(ticket: TicketView): number | null {
        const appliedproducts = this.AppliesTo(ticket);
        if (!appliedproducts){
            return null;
        }

        let _totalprice = 0;
        let _offerprice = this.offer.price || 0;

        for(const [ product, amount ] of appliedproducts.entries()){
            _totalprice += amount * (product.calcinfo.price || 0);

            for (const ticketoption of product.ticketoptions){
                const _option = this.data.alives.get(ticketoption.option) as ProductOptView;
                if (_option){
                    const _charge = _option?.tableprice(ticket.place, ticket.qrcode) ?? 0;
                    _offerprice += amount * _charge;
                    _totalprice += amount * _charge;
                }
            }
        }

        const _savingamount = _totalprice - _offerprice;
        if (_savingamount < 0){
            return null;    // it is more expensive with the offer
        }

        return _savingamount;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type OfferView = ViewType <_OfferView, Offer>;

export function OfferViewProxy(object: Offer, data: dataService): OfferView {
    return new _OfferView(object, data).Proxy as OfferView;
}
